import React, { useState, useRef } from 'react';
import { Helmet } from 'react-helmet-async';
import { Plus, Search, X, Filter, ChevronDown, Headphones } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { useTicketState } from '@/hooks/useTicketState';
import { useAuth } from '@/context/AuthContext';
import { TicketListView } from '@/components/tickets/TicketListView';
import { TicketDetailView } from '@/components/tickets/TicketDetailView';
import { CreateTicketDialog } from '@/components/tickets/CreateTicketDialog';
import { LoadingSpinner } from '@/components/ui/LoadingSpinner';
import { toast } from 'sonner';
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger, DropdownMenuSeparator,
} from '@/components/ui/dropdown-menu';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { TicketStatus, TICKET_STATUS_CONFIG, TICKET_PRIORITY_CONFIG, TicketPriority } from '@/components/tickets/types';

export const PortalSupport: React.FC = () => {
  const { user } = useAuth();
  
  // Ticket state
  const { tickets, loading, createTicket, changeStatus, deleteTicket, reload } = useTicketState();

  // Filter client tickets only
  const clientTickets = tickets.filter(t => t.client_id === user?.id || t.created_by === user?.id);

  // UI state
  const [selectedTicketId, setSelectedTicketId] = useState<string | null>(null);
  const [createOpen, setCreateOpen] = useState(false);
  const [creating, setCreating] = useState(false);
  const [deleteConfirmId, setDeleteConfirmId] = useState<string | null>(null);
  const [deleting, setDeleting] = useState(false);

  // Filter state
  const [searchQuery, setSearchQuery] = useState('');
  const [searchOpen, setSearchOpen] = useState(false);
  const [statusFilter, setStatusFilter] = useState<TicketStatus | 'all'>('all');
  const [priorityFilter, setPriorityFilter] = useState<TicketPriority | 'all'>('all');
  const searchInputRef = useRef<HTMLInputElement>(null);

  React.useEffect(() => { if (searchOpen) searchInputRef.current?.focus(); }, [searchOpen]);

  const handleCreateTicket = async (data: Parameters<typeof createTicket>[0]) => {
    try {
      setCreating(true);
      await createTicket({ ...data, clientId: user?.id });
      toast.success('Ticket created successfully');
      setCreateOpen(false);
    } catch (err: any) {
      toast.error(err.message || 'Failed to create ticket');
    } finally {
      setCreating(false);
    }
  };

  const handleDeleteTicket = async () => {
    if (!deleteConfirmId) return;
    try {
      setDeleting(true);
      await deleteTicket(deleteConfirmId);
      toast.success('Ticket deleted');
      setDeleteConfirmId(null);
      if (selectedTicketId === deleteConfirmId) setSelectedTicketId(null);
    } catch (err: any) {
      toast.error(err.message || 'Failed to delete ticket');
    } finally {
      setDeleting(false);
    }
  };

  // Filtering
  const filteredTickets = clientTickets.filter(t => {
    const matchSearch = !searchQuery ||
      t.subject.toLowerCase().includes(searchQuery.toLowerCase()) ||
      `#${t.ticket_number}`.includes(searchQuery);
    const matchStatus = statusFilter === 'all' || t.status === statusFilter;
    const matchPriority = priorityFilter === 'all' || t.priority === priorityFilter;
    return matchSearch && matchStatus && matchPriority;
  });

  const hasActiveFilters = statusFilter !== 'all' || priorityFilter !== 'all';

  // ── Detail view
  if (selectedTicketId) {
    return (
      <div className="w-full px-6 md:px-10 py-6">
        <Helmet><title>Ticket Detail | Portal</title></Helmet>
        <TicketDetailView
          ticketId={selectedTicketId}
          onBack={() => { setSelectedTicketId(null); reload(); }}
          isTeamMember={false} // Hides internal notes and team features
          members={[]} // Clients don't need members list for assigning
          onTicketUpdated={reload}
        />
      </div>
    );
  }

  return (
    <div className="min-h-screen">
      <Helmet>
        <title>Support | Portal</title>
        <meta name="description" content="Manage your support tickets" />
      </Helmet>

      <div className="w-full px-6 md:px-10 py-6">
        {/* Header */}
        <div className="flex flex-col xl:flex-row xl:items-center justify-between gap-4 mb-8">
          <div>
            <h1 className="text-3xl font-bold text-foreground">Support Tickets</h1>
            <p className="text-sm text-muted-foreground mt-1">
              Create and manage your support requests
            </p>
          </div>

          <div className="flex items-center gap-2">
            {/* Search */}
            <div className="relative flex items-center h-10">
              <AnimatePresence initial={false}>
                {searchOpen ? (
                  <motion.div
                    key="search-open"
                    className="relative flex items-center h-10"
                    initial={{ width: 40, opacity: 0.6 }}
                    animate={{ width: 256, opacity: 1 }}
                    exit={{ width: 40, opacity: 0 }}
                    transition={{ type: 'spring', stiffness: 350, damping: 30 }}
                  >
                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                      <Search className="w-4 h-4 text-muted-foreground" />
                    </div>
                    <input
                      ref={searchInputRef}
                      type="text"
                      value={searchQuery}
                      onChange={e => setSearchQuery(e.target.value)}
                      placeholder="Search tickets..."
                      onBlur={() => { if (!searchQuery) setSearchOpen(false); }}
                      className="w-full h-full pl-9 pr-8 text-sm rounded-lg border transition-colors bg-card border-border focus:ring-1 focus:ring-workspace-accent/30"
                    />
                    <button onClick={() => { setSearchQuery(''); setSearchOpen(false); }} className="absolute inset-y-0 right-0 pr-3 flex items-center text-muted-foreground hover:text-foreground">
                      <X className="w-4 h-4" />
                    </button>
                  </motion.div>
                ) : (
                  <motion.button
                    key="search-icon"
                    onClick={() => setSearchOpen(true)}
                    className="flex items-center justify-center w-10 h-10 rounded-lg border bg-card border-border hover:bg-muted transition-colors"
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                  >
                    <Search className="w-4 h-4" />
                  </motion.button>
                )}
              </AnimatePresence>
            </div>

            {/* Filter */}
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button className={`flex items-center gap-2 px-4 h-10 rounded-lg border transition-colors whitespace-nowrap ${
                  hasActiveFilters ? 'bg-workspace-accent text-white border-transparent' : 'bg-card border-border hover:bg-muted'
                }`}>
                  <Filter className="w-4 h-4" />
                  <span className="text-sm font-medium">Filter</span>
                  <ChevronDown className="w-3.5 h-3.5 opacity-50" />
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="bg-popover border-border w-52">
                <div className="px-2 py-1.5 text-xs font-semibold text-muted-foreground uppercase tracking-wider">Status</div>
                <DropdownMenuItem onClick={() => setStatusFilter('all')} className="flex items-center justify-between">
                  All Statuses {statusFilter === 'all' && <div className="w-2 h-2 bg-workspace-accent rounded-full" />}
                </DropdownMenuItem>
                {Object.entries(TICKET_STATUS_CONFIG).map(([val, cfg]) => (
                  <DropdownMenuItem key={val} onClick={() => setStatusFilter(val as TicketStatus)} className="flex items-center justify-between">
                    <span className="flex items-center gap-2"><span className={`w-2 h-2 rounded-full ${cfg.dotColor}`} />{cfg.label}</span>
                    {statusFilter === val && <div className="w-2 h-2 bg-workspace-accent rounded-full" />}
                  </DropdownMenuItem>
                ))}
                <DropdownMenuSeparator className="bg-border" />
                <div className="px-2 py-1.5 text-xs font-semibold text-muted-foreground uppercase tracking-wider">Priority</div>
                <DropdownMenuItem onClick={() => setPriorityFilter('all')} className="flex items-center justify-between">
                  All Priorities {priorityFilter === 'all' && <div className="w-2 h-2 bg-workspace-accent rounded-full" />}
                </DropdownMenuItem>
                {Object.entries(TICKET_PRIORITY_CONFIG).map(([val, cfg]) => (
                  <DropdownMenuItem key={val} onClick={() => setPriorityFilter(val as TicketPriority)} className="flex items-center justify-between">
                    <span className="flex items-center gap-2"><span className={`w-2 h-2 rounded-full ${cfg.dotColor}`} />{cfg.label}</span>
                    {priorityFilter === val && <div className="w-2 h-2 bg-workspace-accent rounded-full" />}
                  </DropdownMenuItem>
                ))}
                {hasActiveFilters && (
                  <>
                    <DropdownMenuSeparator className="bg-border" />
                    <DropdownMenuItem onClick={() => { setStatusFilter('all'); setPriorityFilter('all'); }} className="text-muted-foreground">
                      Clear Filters
                    </DropdownMenuItem>
                  </>
                )}
              </DropdownMenuContent>
            </DropdownMenu>

            {/* New Ticket */}
            <button
              onClick={() => setCreateOpen(true)}
              className="flex items-center gap-2 px-6 h-10 bg-workspace-accent text-white rounded-lg hover:bg-workspace-accent-hover transition-colors whitespace-nowrap"
            >
              <Plus className="w-4 h-4" />
              <span className="font-medium">New Ticket</span>
            </button>
          </div>
        </div>

        {/* Content */}
        {loading ? (
          null
        ) : filteredTickets.length > 0 ? (
          <TicketListView
            tickets={filteredTickets}
            loading={false}
            onOpenTicket={t => setSelectedTicketId(t.id)}
            onChangeStatus={(id, status) => changeStatus(id, status).catch(e => toast.error(e.message))}
            onDeleteTicket={id => setDeleteConfirmId(id)}
          />
        ) : (
          <div className="flex flex-col items-center justify-center py-20">
            <div className="w-20 h-20 rounded-2xl flex items-center justify-center mb-6 bg-violet-500/10">
              <Headphones className="w-10 h-10 text-violet-500" />
            </div>
            <h2 className="text-xl font-bold mb-2 text-foreground">
              {searchQuery || hasActiveFilters ? 'No tickets found' : 'No tickets yet'}
            </h2>
            <p className="text-center max-w-md mb-6 text-muted-foreground">
              {searchQuery || hasActiveFilters
                ? 'Try adjusting your search or filters'
                : 'Create your first support ticket to get started.'}
            </p>
            {!searchQuery && !hasActiveFilters && (
              <button
               onClick={() => setCreateOpen(true)}
               className="flex items-center gap-2 px-6 h-10 bg-workspace-accent text-white rounded-lg hover:bg-workspace-accent-hover transition-colors"
             >
               <Plus className="w-4 h-4" />
               <span className="font-medium">New Ticket</span>
             </button>
            )}
          </div>
        )}
      </div>

      {/* Create Dialog */}
      <CreateTicketDialog
        open={createOpen}
        onOpenChange={setCreateOpen}
        onSubmit={handleCreateTicket as any}
        clients={[]} // Not needed for client
        members={[]} // Not needed for client
        submitting={creating}
        isClientPortal={true}
      />

      {/* Delete Confirm */}
      <Dialog open={!!deleteConfirmId} onOpenChange={() => setDeleteConfirmId(null)}>
        <DialogContent className="max-w-md bg-card border-border">
          <DialogHeader>
            <DialogTitle className="text-foreground">Delete Ticket</DialogTitle>
          </DialogHeader>
          <p className="text-sm text-muted-foreground py-2">
            This will permanently delete the ticket and all its replies and activity. This action cannot be undone.
          </p>
          <DialogFooter className="gap-3">
            <Button variant="outline" onClick={() => setDeleteConfirmId(null)} className="h-10">Cancel</Button>
            <Button
              onClick={handleDeleteTicket}
              disabled={deleting}
              className="h-10 bg-red-500 hover:bg-red-600 text-white"
            >
              {deleting ? 'Deleting...' : 'Delete Ticket'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};
